import React, { useState } from "react";
import {
  Modal,
  Box,
  Typography,
  Divider,
  IconButton,
  Button,
  Radio,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import { ReactComponent as MoveIconBlack } from "@assets/images/segmentEditor/moveBlockBlack.svg";
import { useDispatch, useSelector } from "react-redux";
import { closeMoveBlockModal } from "../../../../redux/reducers/moveBlockDialog";
import { useEditor } from "@craftjs/core";
import {
  pushToHistoryAction,
  setWheelSegments,
} from "../../../../redux/reducers/segmentBuilder";
import { getSelectedSegment } from "../../../../redux/selectors/segmentBuilderSelectors";
import shortid from "shortid";

const MoveBlockModal = ({ wheelData }) => {
  const dispatch = useDispatch();
  const { isOpen, blockId } = useSelector((state) => state.moveBlockDialog);
  const [selectedSegment, setSelectedSegment] = useState(null);
  const { actions, query } = useEditor();
  const selectedArc = useSelector(getSelectedSegment);

  const handleClose = () => {
    dispatch(closeMoveBlockModal());
    setSelectedSegment(null);
  };

  const handleSegmentSelect = (segmentId) => {
    setSelectedSegment(segmentId);
  };

  const handleMoveOrCopyBlock = ({ type }) => {
    if (!selectedSegment || !blockId) return;

    try {
      // Get the target segment
      const targetSegment = wheelData.find(
        (segment) => segment.id === selectedSegment
      );
      if (!targetSegment) {
        console.error("Target segment not found");
        return;
      }

      // Get the current segment where the block exists
      if (!selectedArc) {
        console.error("Source segment not found");
        return;
      }

      // Parse the nodes data for both segments
      const sourceNodes =
        typeof selectedArc.nodes === "string"
          ? JSON.parse(selectedArc.nodes)
          : selectedArc.nodes;

      const targetNodes =
        typeof targetSegment.nodes === "string"
          ? JSON.parse(targetSegment.nodes)
          : { ...targetSegment.nodes }; // Create a new copy to ensure it's extensible

      // Check if the block exists in the source segment
      if (!sourceNodes[blockId]) {
        console.error("Block not found in source segment");
        return;
      }

      // --- Robust Deep Copy Utility ---
      function deepCopyBlockTree(sourceNodes, rootId, newParentId = "ROOT", oldToNewId = new Map(), allNewNodes = {}) {
        const newId = shortid.generate();
        oldToNewId.set(rootId, newId);

        const oldNode = sourceNodes[rootId];
        if (!oldNode) return { newNodes: {}, newRootId: null };

        // Copy node and update parent
        const newNode = { ...oldNode, parent: newParentId };

        // Recursively copy children in 'nodes'
        if (oldNode.nodes && oldNode.nodes.length > 0) {
          newNode.nodes = oldNode.nodes.map(childId => {
            const { newNodes: childNodes, newRootId: childNewId } = deepCopyBlockTree(sourceNodes, childId, newId, oldToNewId, allNewNodes);
            Object.assign(allNewNodes, childNodes);
            return childNewId;
          });
        }

        // Recursively copy children in 'linkedNodes' (if present)
        if (oldNode.linkedNodes) {
          newNode.linkedNodes = {};
          for (const [key, linkedId] of Object.entries(oldNode.linkedNodes)) {
            const { newNodes: linkedNodes, newRootId: linkedNewId } = deepCopyBlockTree(sourceNodes, linkedId, newId, oldToNewId, allNewNodes);
            Object.assign(allNewNodes, linkedNodes);
            newNode.linkedNodes[key] = linkedNewId;
          }
        }

        allNewNodes[newId] = newNode;
        return { newNodes: allNewNodes, newRootId: newId };
      }

      // Deep copy the block and all its children (including linkedNodes)
      const { newNodes, newRootId } = deepCopyBlockTree(sourceNodes, blockId);

      // Add the new root block to the target segment's ROOT nodes array
      targetNodes.ROOT = {
        ...targetNodes.ROOT,
        nodes: [...(targetNodes.ROOT.nodes || []), newRootId],
      };

      // Merge new nodes into targetNodes
      Object.assign(targetNodes, newNodes);

      // Update the parent reference in the block data - it should point to ROOT for ANY block moved to target because its new parent is ROOT
      targetNodes[newRootId].parent = "ROOT";

      // Process operations in sequence to ensure proper execution
      const finalizeMoveOperation = async () => {
        try {
          // First step: If this is a move operation, we need to remove the block from source nodes
          if (type === "move") {
            // 4. Delete in the editor (visual deletion)
            actions.delete(blockId);

            // Create updated wheel data with source segment updated too
            const updatedWheelData = wheelData.map((segment) => {
              if (segment.id === targetSegment.id) {
                return {
                  ...segment,
                  nodes: targetNodes,
                };
              }
              return segment;
            });

            // Update segments with the new data
            dispatch(setWheelSegments(updatedWheelData));
          } else {
            // For copy operation, we just need to update the target segment
            const updatedWheelData = wheelData.map((segment) => {
              if (segment.id === targetSegment.id) {
                return {
                  ...segment,
                  nodes: targetNodes,
                };
              }
              return segment;
            });

            // Update segments with the new data
            dispatch(setWheelSegments(updatedWheelData));
          }

          dispatch(pushToHistoryAction());

          // Close the modal
          handleClose();
        } catch (err) {
          console.error("Error in finalizeMoveOperation:", err);
        }
      };

      // Execute the operations in sequence
      finalizeMoveOperation();
    } catch (error) {
      console.error("Error moving/copying block:", error);
    }
  };

  console.log("aka", wheelData);

  return (
    <Modal
      open={isOpen}
      onClose={handleClose}
      aria-labelledby="move-block-modal"
      aria-describedby="modal-to-move-block"
      BackdropProps={{
        style: { backgroundColor: "transparent" },
      }}
      disableAutoFocus
    >
      <Box
        sx={{
          position: "absolute",
          width: "276px",
          height: "344px",
          top: "20vh",
          right: "20vw",
          borderRadius: "8px",
          border: "1px solid #e1e1e1",
          background: "white",
          boxShadow: "0 4px 8px rgba(0, 0, 0, 0.1)",
          display: "flex",
          flexDirection: "column",
          padding: "24px",
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              gap: "8px",
            }}
          >
            <MoveIconBlack />
            <Typography
              sx={{
                fontFamily: "Inter",
                fontWeight: "700",
                fontSize: "18px",
                lineHeight: "24px",
                letterSpacing: "0%",
                verticalAlign: "middle",
                color: "#31363c",
                whiteSpace: "nowrap",
              }}
            >
              Move Block
            </Typography>
          </Box>
          <IconButton onClick={handleClose} size="small">
            <CloseIcon sx={{ color: "#31363c" }} />
          </IconButton>
        </Box>

        <Divider
          sx={{
            marginBottom: "2px",
            marginTop: "6px",
            width: "100%",
            color: "#c8d3de",
          }}
        />

        <Box
          sx={{
            flex: 1,
            overflowY: "auto",
          }}
        >
          {wheelData && wheelData.length > 0 ? (
            wheelData
              .filter((segment) => segment.id !== selectedArc?.id)
              .map((segment) => (
                <Box
                  key={segment.id}
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "space-between",
                    cursor: "pointer",
                  }}
                  onClick={() => handleSegmentSelect(segment.id)}
                >
                  <Box
                    sx={{
                      display: "flex",
                      alignItems: "center",
                      gap: "16px",
                      width: "calc(100% - 42px)",
                    }}
                  >
                    <Box
                      sx={{
                        width: "24px",
                        height: "24px",
                        borderRadius: "4px",
                        backgroundColor: segment.settings?.color || "#ccc",
                        flexShrink: 0,
                      }}
                    />
                    <Typography
                      sx={{
                        fontFamily: "Inter",
                        fontWeight: 400,
                        fontSize: "14px",
                        lineHeight: "20px",
                        letterSpacing: "0%",
                        verticalAlign: "middle",
                        color: "#666A6E",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                        whiteSpace: "nowrap",
                        maxWidth: "130px",
                      }}
                      title={segment.settings?.title || "Untitled"}
                    >
                      {segment.settings?.title || "Untitled"}
                    </Typography>
                  </Box>
                  <Radio
                    checked={selectedSegment === segment.id}
                    onChange={() => handleSegmentSelect(segment.id)}
                    sx={{
                      color: "#D9D9D9",
                      "&.Mui-checked": {
                        color: "#8F38EF",
                      },
                    }}
                  />
                </Box>
              ))
          ) : (
            <Typography variant="body2">No segments available</Typography>
          )}
        </Box>

        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            gap: "8px",
            marginTop: "auto",
            paddingTop: "16px",
          }}
        >
          <Button
            onClick={() => handleMoveOrCopyBlock({ type: "copy" })}
            disabled={!selectedSegment}
            sx={{
              height: "40px",
              borderRadius: "4px",
              border: "1px solid #9B5AF8",
              padding: "6px 12px",
              background: "white",
              color: "#9B5AF8",
              textTransform: "none",
              "&:hover": {
                background: "white",
              },
              "&.Mui-disabled": {
                opacity: 0.5,
              },
            }}
          >
            Copy & Move
          </Button>
          <Button
            onClick={() => handleMoveOrCopyBlock({ type: "move" })}
            disabled={!selectedSegment}
            sx={{
              height: "40px",
              borderRadius: "8px",
              padding: "16px",
              gap: "8px",
              background: "#8F38EF",
              color: "white",
              textTransform: "none",
              "&:hover": {
                background: "#8F38EF",
              },
              "&.Mui-disabled": {
                background: "#c8d3de",
                color: "white",
              },
            }}
          >
            Move
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

export default MoveBlockModal;
